import React, { useContext, useState } from "react";
import { FormikErrors, useFormik } from "formik";
import _get from "lodash/get";
import Input from "../forms/Input";
import Button from "../Button";
import { Text } from "@src/components/Text";
import { ModalContext, ModalType } from "./ModalController";
import { color, layout, space } from "styled-system";
import styled from "@emotion/styled";
import axios from "axios";
import { Box, Flex } from "../Boxes";
import { css } from "@emotion/core";
import Translate from "@src/components/translation/Translate";
import { TranslationContext } from "@src/components/translation/TranslationContext";
import loadingMojo from "@src/assets/images/loadingMojo.gif";
import lockedOutMojo from "@src/assets/images/login/locked_out.png";
import { setEntityId } from "@src/utils/logClient";
import { AppDataContext } from "@src/components/AppDataContext";
import window from "global/window";

const Link = styled.a`
  display: inline-block;
  line-height: 18px;
  font-family: proxima-nova, "Helvetica Neue", Helvetica, Arial, sans-serif;
  font-weight: 600;
  color: ${(props) => (props.color ? props.color : "#00B2F7")};
  text-decoration: none;
  cursor: pointer;
  &:hover,
  &:focus {
    text-decoration: none;
    color: ${(props) => (props.hoverColor ? props.hoverColor : "#0092E5")};
  }

  ${layout}
  ${space}
  ${color}
`;

const StyledForm = styled("form")`
  width: 100%;
`;

type ApiAnswerState = "success" | "api-error" | "auth-error" | "auth-lockout" | null;

const AuthErrorResponse = ({ remainingAttempts }: { remainingAttempts: number }) => {
  const loginAttemptsCopy =
    remainingAttempts === 1 ? "components.login.one_attempt" : "components.login.num_of_attempts";
  if (remainingAttempts <= 5)
    return (
      <>
        <Text fontSize="2" fontWeight="bold" color="#2C2A50">
          <Translate path={loginAttemptsCopy} subs={{ attempts: remainingAttempts }} />
        </Text>
        <Text fontSize="2">
          <Translate path="components.login.lockout_warning" />
        </Text>
      </>
    );

  return (
    <>
      <Text fontSize="2" display="inline">
        <Translate path="components.login.invalid_email_or_password" />
      </Text>
    </>
  );
};

const ApiAnswerDetails = ({
  apiAnswer,
  remainingAttempts,
}: {
  apiAnswer: ApiAnswerState;
  remainingAttempts: number;
}) => {
  if (apiAnswer === null) return null;

  const colorCss =
    apiAnswer === "success"
      ? css`
          background-color: #dff0d8;
          border-color: #d6e9c6;
          color: #3c763d;
          border-radius: 5px;
        `
      : css`
          background-color: #ffecee;
          color: #d72a2b;
          border-radius: 30px;
          font-weight: 600;
        `;
  return (
    <Box my={3} p={3} textAlign="center" css={colorCss}>
      {apiAnswer === "success" && (
        <Text fontSize="2">
          <Translate path="components.login.login_success" />
        </Text>
      )}
      {apiAnswer === "api-error" && (
        <>
          <Text fontSize="2" display="inline">
            <Translate path="components.login.login_error" />
          </Text>
          <Link ml="5px" href="https://status.classdojo.com" color="#00bcf2" hoverColor="#00a8d9" display="inline">
            <Translate path="components.login.check_status_page" />
          </Link>
        </>
      )}
      {apiAnswer === "auth-error" && <AuthErrorResponse remainingAttempts={remainingAttempts} />}
    </Box>
  );
};

type LoginFormData = {
  login: string;
  password: string;
};
type LoginFormProps = {
  userType: "teacher" | "parent" | "leader";
  noRedirect?: boolean;
  closeModal?: () => void;
};

const LoginForm: React.FC<LoginFormProps> = ({ userType, noRedirect, closeModal }) => {
  const t = useContext(TranslationContext);
  const modalContext = useContext(ModalContext);
  const appData = useContext(AppDataContext);

  function showForgotPasswordModal() {
    modalContext.showModal(ModalType.ResetPassword);
  }

  function showSignupModal() {
    modalContext.showModal(ModalType.Signup);
  }

  const [apiAnswer, setApiAnswer] = useState<ApiAnswerState>(null);
  const [remainingAttempts, setRemainingAttempts] = useState<number>(undefined);

  const form = useFormik<LoginFormData>({
    initialValues: { login: "", password: "" },
    onSubmit: (values, { setSubmitting }) => {
      const baseUrl =
        process.env.GATSBY_BUILD_WEBSITE === "ideas"
          ? process.env.GATSBY_IDEAS_API_URL
          : userType === "teacher"
          ? process.env.GATSBY_TEACHER_LOGIN_URL
          : process.env.GATSBY_DEFAULT_LOGIN_URL;
      const url = `${baseUrl}/api/session`;
      axios
        .post(
          url,
          {
            ...values,
            resumeAddClassFlow: !!window?.location?.href?.match(/\/connect/),
          },
          {
            withCredentials: true,
          },
        )
        .then((response: any) => {
          setApiAnswer("success");
          const entity = response.teacher || response.parent || response.student;
          if (entity) {
            setEntityId(entity._id);
          }
          if (noRedirect) {
            appData.data.services.getSession();
            modalContext.hideModal();
          } else {
            let redirectUrl = "https://home.classdojo.com/";
            if (response.data.type === "teacher") redirectUrl = "https://teach.classdojo.com";
            else if (response.data.type === "student") redirectUrl = "https://student.classdojo.com";
            window.location.href = redirectUrl;
          }
        })
        .catch((error) => {
          const loginAttemptsLeft = _get(error, "response.headers.remaining-attempts");
          setRemainingAttempts(loginAttemptsLeft === (null || undefined) ? undefined : parseInt(loginAttemptsLeft, 10));
          const errorDetail = _get(error, "response.data.error.detail");
          if (errorDetail === "Incorrect password" || errorDetail === "Incorrect username") {
            setApiAnswer("auth-error");
          } else if (errorDetail === "Too many login attempts, user is temporarily locked out of login") {
            setApiAnswer("auth-lockout");
          } else {
            setApiAnswer("api-error");
          }
        })
        .then(function () {
          setSubmitting(false);
        });
    },
    validate: (values: LoginFormData) => {
      const errors: FormikErrors<LoginFormData> = {};
      if (values.login.length === 0) {
        errors.login = "length";
      }
      if (values.password.length === 0) {
        errors.password = "length";
      }
      return errors;
    },
  });

  if (apiAnswer === "auth-lockout") {
    return (
      <Box my={3} p={3} textAlign="center">
        <img src={lockedOutMojo} />
        <Text fontSize={4} fontWeight={800}>
          <Translate path="components.login.lockout_header" />
        </Text>
        <Text>
          <Translate path="components.login.lockout_desc" />
        </Text>
        <Button onClick={closeModal} width="100%">
          <Translate path="components.login.lockout_button" />
        </Button>
      </Box>
    );
  }

  return (
    <StyledForm onSubmit={form.handleSubmit} data-test-name="login-form">
      {form.isSubmitting && (
        <Box width="50px" height="50px" mb={4} mx="auto">
          <img src={loadingMojo}></img>
        </Box>
      )}
      <Input
        placeholder={t.translate("components.common.email_address") as string}
        autoComplete="email"
        marginBottom={15}
        name="login"
        onChange={form.handleChange}
        value={form.values.login}
        success={!form.errors.login && form.touched.login}
        error={form.errors.login && form.touched.login}
        onBlur={form.handleBlur}
      />
      <Input
        placeholder={t.translate("components.common.password") as string}
        autoComplete="off"
        type="password"
        name="password"
        onChange={form.handleChange}
        value={form.values.password}
        success={!form.errors.password && form.touched.password}
        error={form.errors.password && form.touched.password}
        onBlur={form.handleBlur}
      />
      <Link onClick={showForgotPasswordModal} width="100%" mb="10px">
        {t.translate("components.login.forget_password")}
      </Link>
      <ApiAnswerDetails apiAnswer={apiAnswer} remainingAttempts={remainingAttempts} />
      <Button
        disabled={form.isSubmitting || !form.isValid || !form.dirty}
        type="submit"
        big
        square
        width="100%"
        my="5px"
      >
        {t.translate("pages.home.log_in")}
      </Button>
      <Flex justifyContent="center">
        <Text fontSize={2}> {t.translate("components.login.dont_have_account")}</Text>
        <Link onClick={showSignupModal} ml="10px">
          {t.translate("pages.home.sign_up")}
        </Link>
      </Flex>
    </StyledForm>
  );
};

export default LoginForm;
